import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { ArrowLeft } from "lucide-react";
import { Overline } from "@/components/site/Section";
import CtaBanner from "@/components/site/CtaBanner";
import { formatDate } from "@/pages/Blog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function NotFound() {
  return (
    <div className="container-x py-32 text-center" data-testid="blog-post-not-found">
      <h1 className="font-display text-4xl">Article not found</h1>
      <p className="mt-4 text-muted-foreground">This article may have been moved or unpublished.</p>
      <Link to="/blog" className="mt-8 inline-flex items-center gap-2 text-[hsl(var(--primary))] font-semibold hover:text-[hsl(var(--accent))]">
        <ArrowLeft className="h-4 w-4" /> Back to all articles
      </Link>
    </div>
  );
}

function ArticleHeader({ post }) {
  return (
    <header className="container-x pt-16 md:pt-24 pb-10 max-w-4xl">
      <Link to="/blog" data-testid="blog-post-back" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
        <ArrowLeft className="h-4 w-4" /> All articles
      </Link>
      <Overline className="mb-4">{post.category}</Overline>
      <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.08]" data-testid="blog-post-title">
        {post.title}
      </h1>
      <div className="mt-6 flex items-center gap-3 text-sm text-muted-foreground">
        <span>{post.author}</span>
        <span className="w-1 h-1 rounded-full bg-border" />
        <span>{formatDate(post.created_at)}</span>
        <span className="w-1 h-1 rounded-full bg-border" />
        <span>{Math.max(2, Math.round(post.content.split(/\s+/).length / 220))} min read</span>
      </div>
    </header>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPost(null);
    setError(false);
    axios.get(`${API}/posts/${slug}`)
      .then((r) => {
        setPost(r.data);
        document.title = `${r.data.title} | X-HRverse`;
      })
      .catch(() => setError(true));
    return () => { document.title = "X-HRverse | Building High-Performing Teams for the Modern Workforce"; };
  }, [slug]);

  if (error) return <NotFound />;
  if (!post) return <div className="container-x py-32 text-muted-foreground" data-testid="blog-post-loading">Loading article...</div>;

  return (
    <article data-testid="page-blog-post">
      <ArticleHeader post={post} />
      {post.cover_image && (
        <div className="container-x max-w-5xl pb-12">
          <div className="aspect-[16/8] overflow-hidden rounded-xl border border-border">
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </div>
      )}
      <div className="container-x max-w-3xl pb-16">
        <div className="blog-prose" data-testid="blog-post-content">
          <ReactMarkdown
            components={{
              a: ({ href, children }) =>
                href?.startsWith("/") ? <Link to={href}>{children}</Link> : <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
            }}
          >
            {post.content}
          </ReactMarkdown>
        </div>
      </div>
      <CtaBanner
        title="Ready to put these ideas to work?"
        description="Talk to our workforce experts about your hiring goals."
        ctaLabel="Schedule a Consultation"
        testid="blog-post-cta"
        topPadding={false}
      />
    </article>
  );
}
